import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getTemplates, 
  getTemplate as getTemplateFirestore, 
  createTemplate as createTemplateFirestore, 
  updateTemplate as updateTemplateFirestore, 
  deleteTemplate as deleteTemplateFirestore 
} from '../firebase/unifiedDataService';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Logger } from '../utils/logger';

const log = Logger.create('TemplateContext');

const TemplateContext = createContext(null);

export function TemplateProvider({ children }) {
  const { user } = useUnifiedAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load templates when user changes
  useEffect(() => {
    if (user) {
      loadTemplates();
    } else {
      setTemplates([]);
    }
  }, [user]);

  // Load all templates for the current user from Firestore (single source of truth)
  const loadTemplates = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // Firestore is the single source of truth for templates
      const templatesArray = await getTemplates(user.uid);

      // Sort by lastUsed (most recent first)
      templatesArray.sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0));

      setTemplates(templatesArray);
    } catch (err) {
      log.error('Error loading templates:', err);
      setError('Error loading templates');
    } finally {
      setLoading(false);
    }
  };

  // Get templates for a specific course
  const getTemplatesByCourse = (courseId) => {
    if (!courseId) {
      // Return ungrouped templates (no courseId)
      return templates.filter(t => !t.courseId);
    }
    return templates.filter(t => t.courseId === courseId);
  };

  // Create a new template in Firestore
  const createTemplate = async ({ 
    name, 
    courseId = null, 
    description = '', 
    tags = [], 
    questions = [],
    settings = {} 
  }) => {
    if (!user) throw new Error('Not authenticated');

    try {
      const templateData = {
        name,
        courseId,
        description,
        tags,
        questions,
        userId: user.uid, // For Firestore rules
        owner: user.uid,  // Backwards compatibility
        settings: {
          displayName: name,
          showIntroScreen: true,
          anonymousMode: false,
          enableGamification: true,
          basePointsPerQuestion: 100,
          enableTimeBonus: true,
          enableStreakBonus: false,
          streakBonusPoints: 50,
          enableQuestionTimer: false,
          questionTimerSeconds: 30,
          showLeaderboard: true,
          showStreak: true,
          lockOnReveal: false,
          enableJokers: false,
          enableJoker5050: false,
          enableJokerAudience: false,
          enableJokerBestPlayer: false,
          enableJokerTime: false,
          ...settings
        },
        lastUsed: null,
        useCount: 0
      };

      const templateId = await createTemplateFirestore(templateData);

      // Add to local state
      const newTemplate = { id: templateId, ...templateData };
      setTemplates(prev => [newTemplate, ...prev]);

      return newTemplate;
    } catch (err) {
      log.error('Error creating template:', err);
      throw new Error('Error creating template');
    }
  };

  // Update an existing template in Firestore
  const updateTemplate = async (templateId, updates) => {
    if (!user) throw new Error('Not authenticated');

    try {
      await updateTemplateFirestore(templateId, updates);

      // Update local state
      setTemplates(prev =>
        prev.map(template =>
          template.id === templateId ? { ...template, ...updates, lastModified: Date.now() } : template
        )
      );

      return { id: templateId, ...updates };
    } catch (err) {
      log.error('Error updating template:', err);
      throw new Error('Error updating template');
    }
  };

  // Delete a template from Firestore
  const deleteTemplate = async (templateId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      await deleteTemplateFirestore(templateId);

      // Remove from local state
      setTemplates(prev => prev.filter(template => template.id !== templateId));
    } catch (err) {
      log.error('Error deleting template:', err);
      throw new Error('Error deleting template');
    }
  };

  // Get a single template by ID from Firestore
  const getTemplate = async (templateId) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Check local state first
      const localTemplate = templates.find(t => t.id === templateId);
      if (localTemplate) return localTemplate;

      // If not in local state, fetch from Firestore
      const template = await getTemplateFirestore(templateId);
      return template;
    } catch (err) {
      log.error('Error getting template:', err);
      throw new Error('Error getting template');
    }
  };

  // Duplicate a template
  const duplicateTemplate = async (templateId) => {
    const template = await getTemplate(templateId);
    if (!template) throw new Error('Template not found');

    return createTemplate({
      ...template,
      name: `${template.name} (Copy)`,
      createdAt: Date.now(),
      lastModified: Date.now(),
      lastUsed: null,
      useCount: 0
    });
  };

  // Convert legacy session to template
  const convertSessionToTemplate = async (sessionId, courseId = null) => {
    if (!user) throw new Error('Not authenticated');

    try {
      // Load legacy session
      const sessionRef = ref(database, `sessions/${sessionId}`);
      const snapshot = await get(sessionRef);

      if (!snapshot.exists()) {
        throw new Error('Session not found');
      }

      const session = snapshot.val();

      // Create template from session
      const template = await createTemplate({
        name: session.settings?.displayName || `Quiz ${sessionId}`,
        courseId,
        description: `Converted from session ${sessionId}`,
        tags: [],
        questions: session.questions || [],
        settings: session.settings || {}
      });

      // Add userId to legacy session for ownership
      await update(sessionRef, { userId: user.uid });

      return template;
    } catch (err) {
      log.error('Error converting session:', err);
      throw new Error('Error converting session to template');
    }
  };

  // Increment use count
  const incrementUseCount = async (templateId) => {
    if (!user) return;

    try {
      const templateRef = ref(database, `quiz_templates/${user.uid}/${templateId}`);
      const snapshot = await get(templateRef);
      
      if (snapshot.exists()) {
        const currentCount = snapshot.val().useCount || 0;
        await update(templateRef, {
          useCount: currentCount + 1,
          lastUsed: Date.now()
        });

        // Update local state
        setTemplates(prev =>
          prev.map(t =>
            t.id === templateId
              ? { ...t, useCount: currentCount + 1, lastUsed: Date.now() }
              : t
          )
        );
      }
    } catch (err) {
      log.error('Error incrementing use count:', err);
    }
  };

  const value = {
    templates,
    loading,
    error,
    loadTemplates,
    getTemplatesByCourse,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplate,
    duplicateTemplate,
    convertSessionToTemplate,
    incrementUseCount
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
}

// Custom hook to use template context
export function useTemplates() {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplates must be used within a TemplateProvider');
  }
  return context;
}

export default TemplateContext;
